import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { client } from '@Utils'
import { LTIDashboard as endpoints } from '@URLS'
import { useWatchForError } from '@CommonComponents'
import { STATUS_VARIANTS } from '@LTIDashboard/modules/common'

export const StateContext = createContext()
export function StateProvider({ children, value }) {
  return <StateContext.Provider value={value}>{children}</StateContext.Provider>
}
export function useStateValue() {
  return useContext(StateContext)
}

export const ConnectionContext = createContext({
  status: 'loading',
  connection: { is_connected: 'false' },
})
export function ConnectionProvider({ children }) {
  function reducer(state, action) {
    switch (action.type) {
      case 'status':
        return { ...state, status: action.payload }
      case 'load_connection':
        return { ...state, connection: action.payload, status: 'loaded' }
      case 'componentError':
        return { ...state, componentError: action.payload }
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    status: 'loading',
    connection: { is_connected: 'false' },
    componentError: undefined,
  })
  useWatchForError(state.componentError)
  let params = window.location.pathname.split('/')
  const courseNumber = params[3]
  useEffect(() => {
    async function getConnectionDetails(courseNumber) {
      try {
        const url = endpoints.getConnection()
        const res = await client.get(url, {
          params: { course_number: courseNumber },
        })
        dispatch({ type: 'load_connection', payload: res.data })
      } catch (error) {
        dispatch({ type: 'componentError', payload: error })
      }
    }
    getConnectionDetails(courseNumber)
  }, [])

  return (
    <ConnectionContext.Provider
      value={{ connection: state.connection, status: state.status }}
    >
      {children}
    </ConnectionContext.Provider>
  )
}
export function useConnectionValue() {
  return useContext(ConnectionContext)
}

export const ResourcesContext = createContext()
export function ResourcesProvider({ children }) {
  let params = window.location.pathname.split('/')
  const courseNumber = params[3]
  const [state, dispatch] = useReducer(reducer, {
    status: 'loading',
    resources: [],
    componentError: undefined,
  })
  useEffect(() => {
    async function getResources() {
      try {
        const url = endpoints.getResources()
        const res = await client.get(url, {
          params: { course_number: courseNumber },
        })
        const data = res.data.resources
        dispatch({ type: 'load_resources', payload: data })
        dispatch({ type: 'status', payload: 'loaded' })
      } catch (error) {
        dispatch({ type: 'componentError', payload: error })
      }
    }
    getResources()
  }, [])

  function reducer(state, action) {
    switch (action.type) {
      case 'status':
        return { ...state, status: action.payload }
      case 'load_resources':
        return { ...state, resources: action.payload }
      case 'componentError':
        return { ...state, componentError: action.payload }
    }
  }

  return (
    <ResourcesContext.Provider
      value={{ resources: state.resources, status: state.status }}
    >
      {children}
    </ResourcesContext.Provider>
  )
}
export function useResourcesValue() {
  return useContext(ResourcesContext)
}

export const AssessmentsContext = createContext()
export function AssessmentsProvider({ children }) {
  let params = window.location.pathname.split('/')
  const courseNumber = params[3]
  const [state, dispatch] = useReducer(reducer, {
    request: 'loading',
    assessments: [],
    sectionStatus: 'neutral',
    componentError: undefined,
  })
  useWatchForError(state.componentError)

  useEffect(() => {
    async function getAssessments() {
      try {
        const url = endpoints.getAssessments()
        const res = await client.get(url, {
          params: { course_number: courseNumber },
        })
        setStatusOnAssessments(res.data.assessments)
        dispatch({ type: 'loadAssessments', payload: res.data.assessments })
      } catch (error) {
        console.dir(error)
        dispatch({
          type: 'componentError',
          payload: error,
        })
      }
    }
    getAssessments()
  }, [])

  useEffect(() => {
    function determineSectionStatus() {
      const test = state.assessments.every(item => {
        if (item.scores_synced === 'false' || item.in_canvas === 'false') {
          return false
        } else {
          return true
        }
      })
      const status = test ? 'ok' : 'error'
      dispatch({ type: 'sectionStatus', payload: status })
    }
    determineSectionStatus()
  }, [state.assessments])

  function reducer(state, action) {
    switch (action.type) {
      case 'loadAssessments':
        return { ...state, assessments: action.payload, request: 'loaded' }
      case 'sectionStatus':
        return { ...state, sectionStatus: action.payload }
      case 'componentError':
        return { ...state, componentError: action.payload }
    }
  }
  return (
    <AssessmentsContext.Provider
      value={{
        assessments: state.assessments,
        request: state.request,
        sectionStatus: state.sectionStatus,
      }}
    >
      {children}
    </AssessmentsContext.Provider>
  )
}
export function useAssessmentsValue() {
  return useContext(AssessmentsContext)
}

function setStatusOnAssessments(assessments) {
  assessments.forEach(item => {
    item.individualStatusItems = []
    item.scores_synced
      ? item.individualStatusItems.push(
          item.submissions.length === 0
            ? STATUS_VARIANTS['scoresSynced']['no_submissions']
            : STATUS_VARIANTS['scoresSynced'][
                item.scores_synced === 'true' ? 'ok' : 'error'
              ]
        )
      : undefined
    item.restrict_to_lti
      ? item.individualStatusItems.push(
          STATUS_VARIANTS['accessPath'][
            item.restrict_to_lti === 'true' ? 'restricted' : 'unrestricted'
          ]
        )
      : undefined
    item.aggregateStatus = Object.values(item.individualStatusItems).reduce(
      (status, item) => {
        return item.status === 'error' || status === 'error' ? 'error' : 'ok'
      },
      'ok'
    )
  })
}
